import React, { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FormProps } from "react-bootstrap/Form";
import { BsPrefixRefForwardingComponent } from "react-bootstrap/helpers";
import { FormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router";
import { useHistory } from "react-router-dom";
import { useWindowSize } from "go-core/components/useWindowSize";
import RouteHistory from "go-core/utils/routeHistory";
import { ButtonLoading } from "./ButtonLoading";
import { FormErrorMessageAlert } from "./FormErrorMessageAlert";

interface Props extends FormProps {
	isDirty?: boolean;
	isSubmitting?: boolean;
	errors?: any;
	loading?: boolean;
	buttonSubmitCustom?: JSX.Element;
	backPath?: string;
	formState?: FormState<any>;
}

export const FormDirty: BsPrefixRefForwardingComponent<"form", Props> = (props: Props): JSX.Element => {
	const { isDirty, loading, formState, backPath, buttonSubmitCustom, ...newProps } = props;
	const { isMobile } = useWindowSize();
	const isLoading = !!loading;
	const { t } = useTranslation();
	const history = useHistory();
	const [dirty, setDirty] = useState(isDirty ?? false);
	const [inViewport, setInViewport] = useState(true);
	const formFooterRef = useRef<HTMLDivElement>(null);
	const lastFormElementIsAFieldset = useRef(false);

	useEffect(() => {
		const nodeClass = isMobile ? "page-container" : "content";
		const formContainer = document.querySelector(`.${nodeClass}`);

		if (!formContainer) return;

		if (ResizeObserver) {
			const observer = new ResizeObserver(([entry]) => {
				if (!(entry.target instanceof HTMLElement)) return;

				const {
					target: { children },
				} = entry;
				let childrenTotalHeight = 0;
				Array.from(children).forEach((child: Element) => {
					childrenTotalHeight += child.scrollHeight;
				});
				if (formFooterRef.current) {
					childrenTotalHeight += formFooterRef.current.scrollHeight;
				}
				const currentlyInViewport = childrenTotalHeight <= entry.target.clientHeight;

				setInViewport(currentlyInViewport);
			});

			observer.observe(formContainer, { box: "border-box" });

			return () => {
				observer.disconnect();
			};
		}
	}, [isMobile]);

	useEffect(() => {
		if (!inViewport && formFooterRef.current) {
			const {
				current: { previousSibling },
			} = formFooterRef;

			if (previousSibling instanceof HTMLFieldSetElement) {
				lastFormElementIsAFieldset.current = true;
				previousSibling.style.borderBottom = "none";
			}
		} else if (lastFormElementIsAFieldset.current) {
			const lastFieldset: HTMLFieldSetElement | null = document.querySelector("fieldset:last-of-type");

			if (lastFieldset) {
				lastFieldset.removeAttribute("style");
			}
		}
	}, [inViewport]);

	useEffect(() => {
		setDirty(
			!!(isDirty !== undefined ? isDirty : formState?.isDirty && Object.keys(formState?.dirtyFields).length > 0)
		);
	}, [isDirty, formState?.isDirty, JSON.stringify(formState?.dirtyFields)]);

	const goBack = () => {
		if (backPath && backPath === "RELOAD") {
			RouteHistory.reload(history);
			return;
		}
		RouteHistory.goBack(history, backPath);
	};

	return (
		<Form {...newProps}>
			<Prompt message={t("lib:go_form.form_dirty.message")} when={dirty && !formState?.isSubmitting} />
			<FormErrorMessageAlert errors={props.errors} name="all" />
			{newProps.children}
			{props.loading !== undefined && (
				<>
					{inViewport ? (
						<Form.Group className="form-group">
							{buttonSubmitCustom ? (
								buttonSubmitCustom
							) : (
								<ButtonLoading
									loading={isLoading}
									variant="primary"
									type="submit"
									data-testid="form-save-button"
								>
									{t("lib:common.action.save")}
								</ButtonLoading>
							)}
							{dirty && (
								<Button
									variant="light"
									type="button"
									onClick={() => goBack()}
									data-testid="form-cancel-button"
								>
									{t("lib:common.action.cancel")}
								</Button>
							)}
						</Form.Group>
					) : (
						<div className="form-footer" ref={formFooterRef}>
							<Form.Group className="form-group">
								{buttonSubmitCustom ? (
									buttonSubmitCustom
								) : (
									<ButtonLoading
										loading={isLoading}
										variant="primary"
										type="submit"
										data-testid="form-save-button"
									>
										{t("lib:common.action.save")}
									</ButtonLoading>
								)}
								{dirty && (
									<Button
										variant="light"
										type="button"
										onClick={() => goBack()}
										data-testid="form-cancel-button"
									>
										{t("lib:common.action.cancel")}
									</Button>
								)}
							</Form.Group>
						</div>
					)}
				</>
			)}
		</Form>
	);
};
